/**
 * Law Firms - Cases Page (Task 49)
 */

import { useEffect, useState } from 'react';
import { CaseList } from '@/components/industry/shared/CaseList';
import { CaseForm } from '@/components/industry/shared/CaseForm';
import { useIndustryLegalCases } from '@/hooks/industry';
import { CompanyType } from '@/types/company';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { IndustryLegalCase } from '@/types/industry';
import { MainLayout } from "@/components/MainLayout";

export default function LawFirmsCasesPage() {
  useEffect(() => { document.title = 'Casos Jurídicos | Controle Financeiro'; }, []);

  const { legalCases, isLoading, createLegalCase, updateLegalCase, deleteLegalCase } = useIndustryLegalCases({ domain: CompanyType.LAW_FIRMS });
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCase, setEditingCase] = useState<IndustryLegalCase | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: company } = useQuery({
    queryKey: ['current_company'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data } = await supabase.from('companies').select('*').eq('user_id', user.id).single();
      return data;
    },
  });

  const { data: clients = [] } = useQuery({
    queryKey: ['clients', company?.id],
    queryFn: async () => {
      if (!company) return [];
      const { data } = await supabase.from('clients').select('id, name').eq('company_id', company.id).eq('is_active', true).order('name');
      return data || [];
    },
    enabled: !!company,
  });

  const handleSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      const caseData = {
        client_id: data.client_id,
        start_date: data.start_date.toISOString().split('T')[0],
        status: data.status,
        metadata: {
          caseNumber: data.case_number,
          caseType: data.case_type,
          court: data.court,
          fees: data.fees,
          notes: data.notes,
          clientName: clients.find((c) => c.id === data.client_id)?.name,
        },
      };

      if (editingCase) {
        await updateLegalCase({ id: editingCase.id, data: caseData });
        toast.success('Caso atualizado');
      } else {
        await createLegalCase(caseData);
        toast.success('Caso criado');
      }
      setIsFormOpen(false);
      setEditingCase(undefined);
    } catch (error) {
      toast.error('Erro ao salvar caso');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) return <div className="container mx-auto py-6"><div className="flex items-center justify-center py-12"><div className="text-muted-foreground">Carregando...</div></div></div>;

  return (
<MainLayout>
    <div className="container mx-auto py-6 space-y-6">
      <div><h1 className="text-3xl font-bold">Casos Jurídicos</h1><p className="text-muted-foreground">Gerencie processos e honorários</p></div>
      <CaseList cases={legalCases || []} onEdit={(c) => { setEditingCase(c); setIsFormOpen(true); }} onDelete={async (c) => { if (window.confirm('Excluir caso?')) { await deleteLegalCase(c.id); toast.success('Caso excluído'); } }} onCreate={() => { setEditingCase(undefined); setIsFormOpen(true); }} />
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingCase ? 'Editar' : 'Novo'} Caso</DialogTitle></DialogHeader>
          <CaseForm legalCase={editingCase} clients={clients} onSubmit={handleSubmit} onCancel={() => { setIsFormOpen(false); setEditingCase(undefined); }} isSubmitting={isSubmitting} />
        </DialogContent>
      </Dialog>
    </div>
  
    </MainLayout>);
}

